import orderBy from 'lodash/orderBy'
import creditCardType from 'credit-card-type'
import { Address, Category, CategoryList, MagentoProductPrices, MagentoCreditCardType } from 'lib/types'
import { CATEGORY, COMMA } from 'lib/core/resources/constants'
import { Location } from 'history'

export const getRandomImage = (w = 500, h = 500) => {
  const max = 500
  const randomInt = Math.floor(Math.random() * Math.floor(max))
  return `https://picsum.photos/id/${randomInt}/${w}/${h}`
}

export const makeId = (isNumber?: boolean) => {
  const id = Math.random().toString(36).substr(2, 9)
  return isNumber ? id : ('_' + id)
}

export const getUrlPath = (location: Location) => {
  return location?.pathname?.substring(1)
}

export const stripHtmlTags = (html: string) => {
  return html ? html.replace(/(<([^>]+)>)/ig, '').trim() : undefined
}

export const getFirstLevelCategories = (category: Category) => {
  const isActive = category.isActive
  const isInMenu = category.includeInMenu
  const isFirstLevel = category.isFirstLevel
  return isActive && isInMenu && isFirstLevel
}

export const getChildrenCategories = (category: Category, categoryList: CategoryList): Category[] | undefined => {
  const ids = category?.children
  if (!ids?.trim()) {
    return undefined
  }

  const childrenIds = ids?.split(COMMA)
  const categories: Category[] = []
  childrenIds.forEach(id => {
    const cat: Category | undefined = categoryList.id[id]
    if (cat?.includeInMenu && cat?.isActive) {
      categories.push(cat)
    }
  })

  return categories.length
    ? orderBy(categories, [CATEGORY.POSITION, CATEGORY.NAME], ['asc', 'asc'])
    : undefined
}

export const getTitleTemplate = () => {
  return `%s — ${process.env.REACT_APP_TITLE}`
}

export const slugify = (string: string) => {
  const a = 'àáäâãåăæąçćčđďèéěėëêęǵḧìíïîįłḿǹńňñòóöôœøṕŕřßśšșťțùúüûǘůűūųẃẍÿýźžż·/_,:;'
  const b = 'aaaaaaaaacccddeeeeeeeghiiiiilmnnnnooooooprrssssttuuuuuuuuuwxyyzzz------'
  const p = new RegExp(a.split('').join('|'), 'g')

  return string.toString().toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(p, c => b.charAt(a.indexOf(c))) // Replace special characters
    .replace(/&/g, '-and-') // Replace & with 'and'
    .replace(/[^\w-]+/g, '') // Remove all non-word characters
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, '') // Trim - from end of text
}

export const loadScript = ({ src, async = true }: any) => {
  const script = document.createElement('script')
  const element = document.getElementsByTagName('script')[0]
  script.type = 'text/javascript'
  script.async = async
  script.src = src
  // @ts-expect-error
  element.parentNode.insertBefore(script, element)
}

export const loadStylesheet = ({ href }: any) => {
  const stylesheet = document.createElement('link')
  stylesheet.rel = 'stylesheet'
  stylesheet.href = href
  document.head.insertBefore(stylesheet, document.head.childNodes[document.head.childNodes.length - 1].nextSibling)
}

/**
 * Map "Standard" Types to Magento types
 *
 * Magento: https://github.com/magento/magento2/blob/2.3.5/app/code/Magento/Payment/Model/Method/Cc.php
 * Standard: https://github.com/braintree/credit-card-type/tree/v8.3.0#card-type-constants
 * @param type
 */
export const getCreditCardCode = (type?: string) => {
  switch (type) {
    case creditCardType.types.VISA: {
      return MagentoCreditCardType.Visa
    }
    case creditCardType.types.MASTERCARD: {
      return MagentoCreditCardType.MasterCard
    }
    case creditCardType.types.AMERICAN_EXPRESS: {
      return MagentoCreditCardType.Amex
    }
    case creditCardType.types.DINERS_CLUB: {
      return MagentoCreditCardType.DinersClub
    }
    case creditCardType.types.DISCOVER: {
      return MagentoCreditCardType.Discover
    }
    case creditCardType.types.JCB: {
      return MagentoCreditCardType.JCB
    }
    case creditCardType.types.UNIONPAY: {
      return MagentoCreditCardType.UnionPay
    }
    case creditCardType.types.MAESTRO: {
      return MagentoCreditCardType.Maestro
    }
    case creditCardType.types.ELO: {
      return MagentoCreditCardType.ELO
    }
    case creditCardType.types.MIR: {
      return MagentoCreditCardType.MIR
    }
    case creditCardType.types.HIPERCARD: {
      return MagentoCreditCardType.Hipercard
    }
    default: {
      return MagentoCreditCardType.Other
    }
  }
}

/**
 * Map Magento Types to "Standard" types
 *
 * Magento: https://github.com/magento/magento2/blob/2.3.5/app/code/Magento/Payment/Model/Method/Cc.php
 * Standard: https://github.com/braintree/credit-card-type/tree/v8.3.0#card-type-constants
 * @param code
 */
export const getCreditCardType = (code?: string) => {
  switch (code) {
    case MagentoCreditCardType.Visa: {
      return creditCardType.types.VISA
    }
    case MagentoCreditCardType.MasterCard: {
      return creditCardType.types.MASTERCARD
    }
    case MagentoCreditCardType.Amex: {
      return creditCardType.types.AMERICAN_EXPRESS
    }
    case MagentoCreditCardType.DinersClub: {
      return creditCardType.types.DINERS_CLUB
    }
    case MagentoCreditCardType.Discover: {
      return creditCardType.types.DISCOVER
    }
    case MagentoCreditCardType.JCB: {
      return creditCardType.types.JCB
    }
    case MagentoCreditCardType.UnionPay: {
      return creditCardType.types.UNIONPAY
    }
    case MagentoCreditCardType.Maestro: {
      return creditCardType.types.MAESTRO
    }
    case MagentoCreditCardType.ELO: {
      return creditCardType.types.ELO
    }
    case MagentoCreditCardType.MIR: {
      return creditCardType.types.MIR
    }
    case MagentoCreditCardType.Hipercard: {
      return creditCardType.types.HIPERCARD
    }
    default: {
      return creditCardType.types.OTHER
    }
  }
}

type AddressKeys = keyof Address
type AddressObject = Partial<Pick<any, AddressKeys>>

const addressKeys: AddressKeys[] = [
  'city',
  'company',
  'countryCode',
  'firstName',
  'lastName',
  'postalCode',
  'regionCode',
  'street',
  'street2',
  'phoneNumber'
]

export const addressMatch = (address1?: AddressObject, address2?: AddressObject): boolean => {
  if (!address1 || !address2) {
    return false
  }

  const match = addressKeys.reduce<boolean>((matches, key) => {
    matches = matches && address1?.[key] === address2?.[key]
    return matches
  }, true)

  return match
}

export const addressIsEmpty = (address?: AddressObject): boolean => {
  const {
    countryCode,
    firstName,
    lastName,
    street
  } = address ?? {}

  return (!countryCode || !firstName || !lastName || !street)
}

/**
 * @param str
 */
export const capUnitedStatesZipCode = (str?: string) => {
  return str?.slice(0, 5) ?? ''
}

export const getPriceValues = (price: MagentoProductPrices): [number, string] => {
  const { maximalPrice } = price || {}
  const { amount: amountMaximal } = maximalPrice || {}
  const { value, currency } = amountMaximal || {}

  return [
    value,
    currency
  ]
}
